import React, { FC, ReactNode, useCallback, useEffect, useState } from "react";
import styles from "./accordion.module.css";
import cn from "classnames";
import { DownOutlined } from "@ant-design/icons";

interface AccordionProps {
  chevronRight?: boolean;
  children?: ReactNode;
  className?: string;
  header?: ReactNode;
  headerBefore?: ReactNode;
  isCollapsed?: boolean;
  isDisabled?: boolean;
  isCollapsedBodyRendered?: boolean;
  onChange?: (isCollapsed: boolean) => void;
  onToggleCollapse?: () => void;
}

const Accordion: FC<AccordionProps> = ({
  chevronRight = false,
  children = null,
  className = "",
  header = null,
  headerBefore = null,
  isCollapsedBodyRendered = false,
  isCollapsed: propIsCollapsed = false,
  isDisabled = false,
  onChange,
  onToggleCollapse,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(propIsCollapsed);

  useEffect(() => {
    if (onToggleCollapse) {
      setIsCollapsed(propIsCollapsed);
    }
  }, [propIsCollapsed, onToggleCollapse]);

  const toggleCollapse = useCallback(() => {
    if (!isDisabled) {
      if (onToggleCollapse) {
        onToggleCollapse();
      } else {
        setIsCollapsed((prev) => {
          const newCollapsed = !prev;
          onChange?.(newCollapsed);
          return newCollapsed;
        });
      }
    }
  }, [isDisabled, onToggleCollapse, onChange]);

  return (
    <div
      className={cn(styles.stenAccordion, className, {
        [styles.stenAccordionCollapsed]: isCollapsed,
        [styles.stenAccordionDisabled]: isDisabled,
      })}
    >
      <header
        role="button"
        data-testid="accordion-header"
        className={cn(styles.stenAccordionHeader, {
          [styles.stenAccordionHeaderChevronRight]: chevronRight,
        })}
        onClick={toggleCollapse}
      >
        {headerBefore}
        <DownOutlined className={styles.stenAccordionChevron} />
        {header}
      </header>
      {(isCollapsedBodyRendered || !isCollapsed) && <section className={styles.stenAccordionBody}>{children}</section>}
    </div>
  );
};

export default Accordion;
